import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import { DocsIcon } from './docsIcons'

/**
 * The Bayes Price website's docs frame (bayesprice-website
 * src/components/DocsLayout.tsx), rebuilt in MUI for the public /help page so
 * Platinum's docs look like the rest of bayesprice.com/docs: a fixed white
 * header with the logo and a "› Docs › Platinum" breadcrumb, a pale grey
 * sidebar, and a content column. Sizes and colours are the website's
 * computed values (Tailwind v4: its gray and blue-600 are oklch colours).
 * Inter and Outfit load only while this frame is mounted.
 */

export const DOCS = {
  charcoal: '#332E33',
  muted: '#6b7280',
  hairline: '#e5e5e5',
  gray50: 'oklch(98.5% 0.002 247.839)',
  gray100: 'oklch(96.7% 0.003 264.542)',
  gray200: 'oklch(92.8% 0.006 264.531)',
  gray400: 'oklch(70.7% 0.022 261.325)',
  gray500: 'oklch(55.1% 0.027 264.364)',
  gray600: 'oklch(44.6% 0.03 256.802)',
  gray900: 'oklch(21% 0.034 264.665)',
  /** Platinum's product colour on the website: Tailwind blue-600. */
  blue: 'oklch(54.6% 0.245 262.881)',
  blueTint: 'oklch(54.6% 0.245 262.881 / 0.1)',
  sans: "'Inter', system-ui, sans-serif",
  heading: "'Outfit', system-ui, sans-serif",
} as const

/** Tailwind's sm and lg breakpoints, which the website's layout switches on. */
export const SM = '@media (min-width: 640px)'
export const LG = '@media (min-width: 1024px)'

const WEBSITE = 'https://www.bayesprice.com'
const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Outfit:wght@400;500;600;700&display=swap'

export interface DocsNavLink {
  title: string
  href: string
  active: boolean
  /** Called on a plain left click instead of following the href. */
  onSelect?: () => void
}

export interface DocsNavItem extends DocsNavLink {
  /** Shown indented under the item while it is the active one. */
  children?: DocsNavLink[]
}

/** A plain left click, which a page may handle itself; any modifier keeps the browser's behaviour (new tab, etc.). */
function isPlainClick(e: React.MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented
}

/** Adds the website's fonts while the docs are on screen, and removes them after. */
function useDocsFonts() {
  useEffect(() => {
    const links = [
      Object.assign(document.createElement('link'), { rel: 'preconnect', href: 'https://fonts.googleapis.com' }),
      Object.assign(document.createElement('link'), { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' }),
      Object.assign(document.createElement('link'), { rel: 'stylesheet', href: FONTS_HREF }),
    ]
    links.forEach((l) => document.head.appendChild(l))
    return () => links.forEach((l) => l.remove())
  }, [])
}

const focusRing = { outline: `2px solid ${DOCS.blue}`, outlineOffset: '2px', borderRadius: '2px' }

const headerLinkSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '14px',
  lineHeight: '20px',
  color: DOCS.gray600,
  textDecoration: 'none',
  transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': { color: DOCS.gray900 },
}

const SidebarLink: React.FC<{ link: DocsNavLink; child?: boolean; current?: boolean; onClose: () => void }> = ({ link, child, current, onClose }) => (
  <Box
    component="a"
    href={link.href}
    aria-current={current ? (child ? 'location' : 'page') : undefined}
    onClick={(e: React.MouseEvent) => {
      if (link.onSelect && isPlainClick(e)) {
        e.preventDefault()
        link.onSelect()
      }
      onClose()
    }}
    sx={{
      display: 'block',
      py: '8px',
      pl: child ? '16px' : 0,
      fontSize: '14px',
      lineHeight: '20px',
      textDecoration: 'none',
      transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
      ...(current
        ? { color: DOCS.blue, fontWeight: 500 }
        : { color: DOCS.gray600, fontWeight: 400, '&:hover': { color: DOCS.gray900 } }),
    }}
  >
    {link.title}
  </Box>
)

export interface DocsLayoutProps {
  nav: DocsNavItem[]
  /** The scrolling element, so the page can scroll it to the top or to an anchor. */
  scrollRef?: React.Ref<HTMLDivElement>
  children: React.ReactNode
}

const DocsLayout: React.FC<DocsLayoutProps> = ({ nav, scrollRef, children }) => {
  useDocsFonts()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const close = () => setSidebarOpen(false)

  return (
    <Box
      ref={scrollRef}
      onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Escape' && sidebarOpen) setSidebarOpen(false) }}
      sx={{
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        bgcolor: '#ffffff',
        color: DOCS.charcoal,
        fontFamily: DOCS.sans,
        fontSize: '16px',
        lineHeight: 1.6,
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        '& a:focus-visible, & button:focus-visible': focusRing,
      }}
    >
      <Box
        component="a"
        href="#docs-content"
        sx={{
          position: 'fixed', top: 8, left: 8, zIndex: 1300, px: '12px', py: '8px', bgcolor: '#fff',
          color: DOCS.blue, fontSize: '14px', border: `1px solid ${DOCS.gray200}`, borderRadius: '8px',
          transform: 'translateY(-200%)', '&:focus': { transform: 'none' },
        }}
      >
        Skip to content
      </Box>

      {/* Top navigation */}
      <Box component="header" sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1200, bgcolor: '#fff', borderBottom: `1px solid ${DOCS.gray200}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', px: '16px', [LG]: { px: '32px' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <Box
              component="button"
              type="button"
              aria-label={sidebarOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={sidebarOpen}
              aria-controls="docs-sidebar"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              sx={{
                display: 'flex', p: '8px', ml: '-8px', border: 0, bgcolor: 'transparent', cursor: 'pointer',
                color: DOCS.gray600, '&:hover': { color: DOCS.gray900 }, [LG]: { display: 'none' },
              }}
            >
              <DocsIcon name={sidebarOpen ? 'x' : 'menu'} size={20} />
            </Box>
            <Box component="a" href={WEBSITE} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box component="img" src="/img/bayes-price-logo.svg" alt="Bayes Price" sx={{ height: '24px', width: 'auto', display: 'block' }} />
            </Box>
            <Box
              component="nav"
              aria-label="Breadcrumb"
              sx={{ display: 'none', alignItems: 'center', gap: '8px', fontSize: '14px', lineHeight: '20px', color: DOCS.gray500, [SM]: { display: 'flex' } }}
            >
              <DocsIcon name="chevron-right" />
              <Box component="a" href={`${WEBSITE}/docs`} sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: DOCS.gray900 } }}>Docs</Box>
              <DocsIcon name="chevron-right" />
              <Box component="span" aria-current="page" sx={{ color: DOCS.blue }}>Platinum</Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Box component="a" href={`${WEBSITE}/docs`} aria-label="All Docs" sx={headerLinkSx}>
              <DocsIcon name="book" />
              <Box component="span" sx={{ display: 'none', [SM]: { display: 'inline' } }}>All Docs</Box>
            </Box>
            <Box component="a" href={WEBSITE} aria-label="Home" sx={headerLinkSx}>
              <DocsIcon name="home" />
              <Box component="span" sx={{ display: 'none', [SM]: { display: 'inline' } }}>Home</Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Sidebar: slides in on small screens, always shown from lg */}
      <Box
        component="aside"
        id="docs-sidebar"
        data-open={sidebarOpen ? 'true' : 'false'}
        sx={{
          position: 'fixed', top: '64px', left: 0, bottom: 0, width: '288px', zIndex: 1150,
          bgcolor: DOCS.gray50, borderRight: `1px solid ${DOCS.gray200}`, overflowY: 'auto',
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          visibility: sidebarOpen ? 'visible' : 'hidden',
          transition: sidebarOpen
            ? 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)'
            : 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear 150ms',
          [LG]: { transform: 'translateX(0)', visibility: 'visible' },
        }}
      >
        <Box sx={{ p: '24px' }}>
          <Box sx={{ fontFamily: DOCS.heading, fontSize: '18px', lineHeight: '28px', fontWeight: 600, color: DOCS.blue, mb: '24px' }}>
            Platinum Documentation
          </Box>
          <Box component="nav" aria-label="Platinum Documentation">
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, '& > li + li': { mt: '4px' } }}>
              {nav.map((item) => (
                <li key={item.href}>
                  <SidebarLink link={item} current={item.active} onClose={close} />
                  {item.active && !!item.children?.length && (
                    <Box component="ul" sx={{ listStyle: 'none', m: 0, ml: '12px', pl: '12px', borderLeft: `1px solid ${DOCS.gray200}` }}>
                      {item.children.map((child) => (
                        <li key={child.href}>
                          <SidebarLink link={child} child current={child.active} onClose={close} />
                        </li>
                      ))}
                    </Box>
                  )}
                </li>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Overlay behind the open sidebar on small screens */}
      {sidebarOpen && (
        <Box
          onClick={close}
          aria-hidden="true"
          sx={{ position: 'fixed', inset: 0, zIndex: 1100, bgcolor: 'rgba(0, 0, 0, 0.2)', [LG]: { display: 'none' } }}
        />
      )}

      <Box component="main" id="docs-content" tabIndex={-1} sx={{ pt: '64px', outline: 'none', [LG]: { pl: '288px' } }}>
        <Box sx={{ maxWidth: '896px', mx: 'auto', px: '24px', py: '48px', [LG]: { px: '32px' } }}>
          {children}
        </Box>
      </Box>
    </Box>
  )
}

export default DocsLayout
